import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, FormGroup, Row, Col } from 'reactstrap';
import { getProfileQuestionDetails, updateProfileQuestion } from './store/index';
import Swal from 'sweetalert2';
import { ComponentLoader } from '../common/Loader';
import Select from 'react-select';
import './ProfileQuestions.css';

const EditProfileQuestion = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(true);

    const [formData, setFormData] = useState({
        title: "",
        description: "",
        inputType: "single_select",
        options: [""],
        gender: "all",
        ageMin: 0,
        ageMax: 120,
        priority: 1,
        isRequired: true,
        allowSkip: true,
        status: "active",
        frequencyType: "once",
        startDate: "",
        endDate: ""
    });

    useEffect(() => {
        if (id) {
            dispatch(getProfileQuestionDetails(id)).then((res) => {
                if (res.payload && res.payload.response === 'S') {
                    const crypto = require('../../../services/crypto');
                    const decryptedData = crypto.AesDecrypt(res.payload.data);
                    
                    setFormData({
                        title: decryptedData.title || "",
                        description: decryptedData.description || "",
                        inputType: decryptedData.inputType || "single_select",
                        options: (decryptedData.options && decryptedData.options.length > 0) ? decryptedData.options : [""],
                        gender: (decryptedData.targetAudience && decryptedData.targetAudience.gender && decryptedData.targetAudience.gender[0]) ? decryptedData.targetAudience.gender[0] : "all",
                        ageMin: (decryptedData.targetAudience && decryptedData.targetAudience.ageMin) || 0,
                        ageMax: (decryptedData.targetAudience && decryptedData.targetAudience.ageMax) || 120,
                        priority: decryptedData.priority || 1,
                        isRequired: decryptedData.isRequired !== undefined ? decryptedData.isRequired : true,
                        allowSkip: decryptedData.allowSkip !== undefined ? decryptedData.allowSkip : true,
                        status: decryptedData.status || "active",
                        frequencyType: (decryptedData.frequency && decryptedData.frequency.type) ? decryptedData.frequency.type : "once",
                        startDate: decryptedData.startDate ? new Date(decryptedData.startDate).toISOString().split('T')[0] : "",
                        endDate: decryptedData.endDate ? new Date(decryptedData.endDate).toISOString().split('T')[0] : ""
                    });
                } else {
                    Swal.fire("Error", "Failed to load question details", "error");
                }
                setIsLoading(false);
            });
        }
    }, [dispatch, id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === "checkbox" ? checked : value
        });
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData({
            ...formData,
            [name]: selectedOption ? selectedOption.value : ''
        });
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '20px',
            borderColor: '#e1e5eb',
            boxShadow: 'none',
            padding: '0 5px',
            minHeight: '38px',
            height: '38px',
            backgroundColor: 'transparent',
            '&:hover': {
                borderColor: '#c5c9ce'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            backgroundColor: state.isSelected ? '#f4f6f9' : 'white',
            color: '#495057',
            '&:hover': {
                backgroundColor: '#f8f9fa'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            color: '#000'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    const formatOptionLabel = ({ label, color }) => (
        <div style={{ display: "flex", alignItems: "center" }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, marginRight: 8, display: 'inline-block' }}></span>
            {label}
        </div>
    );

    const inputTypeOptions = [
        { value: "text", label: "Text Input", color: "#6c757d" },
        { value: "number", label: "Number Input", color: "#6c757d" },
        { value: "single_select", label: "Single Select (Radio)", color: "#17a2b8" },
        { value: "multi_select", label: "Multi Select (Checkbox)", color: "#17a2b8" }
    ];

    const frequencyOptions = [
        { value: "once", label: "Ask Once", color: "#28a745" },
        { value: "every_30_days", label: "Every 30 Days", color: "#ffc107" },
        { value: "every_90_days", label: "Every 90 Days", color: "#ffc107" },
        { value: "yearly", label: "Yearly", color: "#007bff" }
    ];

    const genderOptions = [
        { value: "all", label: "All", color: "#6c757d" },
        { value: "male", label: "Male", color: "#007bff" },
        { value: "female", label: "Female", color: "#e83e8c" },
        { value: "other", label: "Other", color: "#17a2b8" }
    ];

    const statusOptions = [
        { value: "active", label: "Active", color: "#28a745" },
        { value: "inactive", label: "Inactive", color: "#dc3545" }
    ];

    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = value;
        setFormData({ ...formData, options: newOptions });
    };

    const addOption = () => {
        setFormData({ ...formData, options: [...formData.options, ""] });
    };

    const removeOption = (index) => {
        const newOptions = formData.options.filter((_, i) => i !== index);
        setFormData({ ...formData, options: newOptions });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (["single_select", "multi_select"].includes(formData.inputType)) {
            const validOptions = formData.options.filter(o => o.trim() !== "");
            if (validOptions.length === 0) {
                Swal.fire('Error', 'Please provide at least one option', 'error');
                return;
            }
        }

        const payload = {
            id,
            ...formData,
            options: ["single_select", "multi_select"].includes(formData.inputType) 
                ? formData.options.filter(o => o.trim() !== "") 
                : [],
            gender: [formData.gender]
        };

        dispatch(updateProfileQuestion(payload)).then((res) => {
            if (res.payload && res.payload.response === 'S') {
                Swal.fire('Success', 'Question updated successfully. Version incremented.', 'success');
                sessionStorage.removeItem('PROFILE_QUESTIONS_CACHE');
                navigate('/profile-questions');
            } else {
                Swal.fire('Error', res.payload?.message || 'Failed to update question', 'error');
            }
        });
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <ComponentLoader color="#4f46e5" size="40px" />
            </div>
        );
    }

    return (
        <div className="container_alluser pt-3">
            <div className="d-flex align-items-center position-relative mt-4 mb-3 tittle_container">
                <div className="position-absolute" style={{ left: 0 }}>
                    <Button color="secondary" onClick={() => navigate('/profile-questions')} className="pq-btn-back">{t('Back')}</Button>
                </div>
                <div className="w-100 text-center mainheading m-0">
                    {t('Edit Profile Question')}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 bg-white rounded shadow-sm">
                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Title')} *</Label>
                            <Input type="text" name="title" value={formData.title} onChange={handleChange} required className="pq-input-text" />
                        </FormGroup>
                    </Col>

                    <Col md={12}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Description')}</Label>
                            <Input type="textarea" name="description" value={formData.description} onChange={handleChange} rows="2" className="pq-input-text" />
                        </FormGroup>
                    </Col>
                </Row>

                <Row className="mt-2">
                    <Col md={3}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Input Type')} *</Label>
                            <Select
                                options={inputTypeOptions}
                                value={inputTypeOptions.find(o => o.value === formData.inputType)}
                                onChange={(option) => handleSelectChange('inputType', option)}
                                styles={customSelectStyles}
                                formatOptionLabel={formatOptionLabel}
                                isSearchable={false}
                            />
                        </FormGroup>
                    </Col>

                    <Col md={3}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Frequency')} *</Label>
                            <Select
                                options={frequencyOptions}
                                value={frequencyOptions.find(o => o.value === formData.frequencyType)}
                                onChange={(option) => handleSelectChange('frequencyType', option)}
                                styles={customSelectStyles}
                                formatOptionLabel={formatOptionLabel}
                                isSearchable={false}
                            />
                        </FormGroup>
                    </Col>

                    <Col md={3}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Target Gender')}</Label>
                            <Select
                                options={genderOptions}
                                value={genderOptions.find(o => o.value === formData.gender)}
                                onChange={(option) => handleSelectChange('gender', option)}
                                styles={customSelectStyles}
                                formatOptionLabel={formatOptionLabel}
                                isSearchable={false}
                            />
                        </FormGroup>
                    </Col>

                    <Col md={3}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Status')}</Label>
                            <Select
                                options={statusOptions}
                                value={statusOptions.find(o => o.value === formData.status)}
                                onChange={(option) => handleSelectChange('status', option)}
                                styles={customSelectStyles}
                                formatOptionLabel={formatOptionLabel}
                                isSearchable={false}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row className="mt-2">
                     <Col md={3}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="pq-label-sm">{t('Start Date')}</Label>
                                    <Input type="date" name="startDate" value={formData.startDate} onChange={handleChange} className="pq-input-pill" />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="pq-label-sm">{t('End Date')}</Label>
                                    <Input type="date" name="endDate" value={formData.endDate} onChange={handleChange} className="pq-input-pill" />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                     <Col md={3}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="pq-label-sm">{t('Age Min')}</Label>
                                    <Input type="number" name="ageMin" value={formData.ageMin} onChange={handleChange} min="0" className="pq-input-pill" />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="pq-label-sm">{t('Age Max')}</Label>
                                    <Input type="number" name="ageMax" value={formData.ageMax} onChange={handleChange} max="120" className="pq-input-pill" />
                                </FormGroup>
                            </Col>
                        </Row>
                    </Col>
                    <Col md={3}>
                        <FormGroup>
                            <Label className="pq-label-sm">{t('Priority (Higher = Shows First)')}</Label>
                            <Row>
                                <Col md={6}>
                                    <Input type="number" name="priority" value={formData.priority} onChange={handleChange} min="1" className="pq-input-pill" />
                                </Col>
                            </Row>
                        </FormGroup>
                    </Col>

                    <Col md={3} className="d-flex align-items-center">
                        <FormGroup check className="pq-checkbox-group-me">
                            <Label check className="d-flex align-items-center m-0 pq-checkbox-label">
                                <Input type="checkbox" name="isRequired" checked={formData.isRequired} onChange={handleChange} className="pq-checkbox-input" />
                                <span>{t('Required Answer')}</span>
                            </Label>
                        </FormGroup>
                        <FormGroup check className="pq-checkbox-group">
                            <Label check className="d-flex align-items-center m-0 pq-checkbox-label">
                                <Input type="checkbox" name="allowSkip" checked={formData.allowSkip} onChange={handleChange} className="pq-checkbox-input" />
                                <span>{t('Allow Skip')}</span>
                            </Label>
                        </FormGroup>
                    </Col>
                </Row>

                {["single_select", "multi_select"].includes(formData.inputType) && (
                    <Row>
                        <Col md={6}>
                            <div className="mt-4 p-3 border rounded bg-light">
                                <h6 className="pq-options-heading">{t('Options')}</h6>
                                {formData.options.map((option, index) => (
                                    <Row key={index} className="mb-2">
                                        <Col md={10}>
                                            <Input 
                                                type="text" 
                                                value={option} 
                                                onChange={(e) => handleOptionChange(index, e.target.value)} 
                                                placeholder={`Option ${index + 1}`}
                                                className="pq-input-pill"
                                            />
                                        </Col>
                                        <Col md={2}>
                                            <Button color="danger" onClick={() => removeOption(index)} disabled={formData.options.length === 1} className="d-flex align-items-center justify-content-center pq-btn-trash">
                                                <i className="fa fa-trash"></i>
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button className="mt-2 pq-btn-add-option" onClick={addOption}>
                                    <i className="fa fa-plus"></i> {t('Add Option')}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                )}

                <div className="mt-4 pq-save-container">
                    <Button type="submit" className="pq-btn-save">{t('Update Question')}</Button>
                </div>
            </form>
        </div>
    );
};

export default EditProfileQuestion;
